import React, { useState, useEffect } from 'react';
import { ArrowRight, Pencil } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { Ideation } from '../Ideation';
import { VideoWorkspace } from '../VideoWorkspace';
import { VideoUploader } from '../VideoUploader';
import { CollaboratorsPanel } from './CollaboratorsPanel';
import { WorkflowSpend } from '../Billing/WorkflowSpend';
import { ProjectStepper } from '../ProjectStepper';
import { ProjectReport } from '../ProjectReport';
import {
    GetWorkflow,
    FinalizeWorkflowStep,
    GetVideoWorkspaceOutputUrl,
    GetAnalysisById,
    RenameWorkflow,
    CreateWorkflow
} from '../../Client/ManagementServiceClient';
import './styles.css';

const STEP_ORDER = ['idea', 'workspace', 'post', 'report'];

const normalizeWorkflowStep = (step) => {
    if (step === 'analysis') {
        return 'post';
    }
    return step;
};

const isStepFinalized = (finalizedSteps, step) => {
    if (finalizedSteps[step]) {
        return true;
    }
    // 'analysis' is the transitional alias for the post step.
    return step === 'post' && Boolean(finalizedSteps.analysis);
};

// Which step to open a project on.
//
// CurrentStep can lag behind what has actually been done — posts finalized
// before the server learned to advance past the Post step left projects
// opening onto Post forever, even though they were already published. Walking
// past finalized steps repairs those without a data migration.
const resolveLandingStep = (workflowData) => {
    const finalized = workflowData?.FinalizedSteps || {};
    let index = STEP_ORDER.indexOf(normalizeWorkflowStep(workflowData?.CurrentStep || 'idea'));
    if (index < 0) {
        index = 0;
    }
    while (index < STEP_ORDER.length - 1 && isStepFinalized(finalized, STEP_ORDER[index])) {
        index += 1;
    }
    return STEP_ORDER[index];
};

const parseJsonField = (value) => {
    if (value && typeof value === 'object') {
        return value;
    }
    if (typeof value !== 'string') {
        return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }

    const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i);
    const candidate = fenced?.[1]?.trim() || trimmed;

    const repairCommonLLMJsonIssues = (text) => {
        if (typeof text !== 'string') return text;

        const lines = text.split('\n');
        const repairedLines = lines.map((line) => {
            const match = line.match(/^(\s*"[^"]+"\s*:\s*")(.*)("\s*,?\s*)$/);
            if (!match) return line;

            const [, prefix, rawValue, suffix] = match;
            const escapedValue = rawValue.replace(/(?<!\\)"/g, '\\"');
            return `${prefix}${escapedValue}${suffix}`;
        });

        return repairedLines.join('\n');
    };

    const tryParse = (text) => {
        const parsed = JSON.parse(text);
        if (typeof parsed === 'string') {
            try {
                return JSON.parse(parsed);
            } catch {
                return null;
            }
        }
        return parsed;
    };

    try {
        const parsed = tryParse(candidate);
        if (parsed) return parsed;
    } catch {
        const repairedCandidate = repairCommonLLMJsonIssues(candidate);
        if (repairedCandidate !== candidate) {
            try {
                const repairedParsed = tryParse(repairedCandidate);
                if (repairedParsed) return repairedParsed;
            } catch {
                // Continue with object extraction fallback.
            }
        }

        const objectMatch = candidate.match(/\{[\s\S]*\}/);
        if (!objectMatch) {
            return null;
        }

        try {
            const parsed = tryParse(objectMatch[0]);
            if (parsed) return parsed;
        } catch {
            const repairedExtracted = repairCommonLLMJsonIssues(objectMatch[0]);
            if (repairedExtracted === objectMatch[0]) {
                return null;
            }

            try {
                const repairedParsed = tryParse(repairedExtracted);
                if (repairedParsed) return repairedParsed;
            } catch {
                return null;
            }
        }
    }

    return null;
};

const extractMetadataFromRawString = (rawMetadata) => {
    const text = String(rawMetadata || '');
    if (!text.trim()) {
        return { title: '', description: '', tags: [] };
    }

    const titleMatch = text.match(/"title"\s*:\s*"([\s\S]*?)"\s*,/i);
    const descriptionMatch = text.match(/"description"\s*:\s*"([\s\S]*?)"\s*,\s*"tags"/i);
    const tagsMatch = text.match(/"tags"\s*:\s*\[([\s\S]*?)\]/i);

    const title = titleMatch ? titleMatch[1].replace(/\\"/g, '"').trim() : '';
    const description = descriptionMatch ? descriptionMatch[1].replace(/\\"/g, '"').trim() : '';

    const tags = tagsMatch
        ? (tagsMatch[1].match(/"([^"]+)"/g) || [])
            .map((part) => part.replace(/^"|"$/g, '').trim())
            .filter(Boolean)
        : [];

    return { title, description, tags };
};

const extractMetadataFromAnalysis = (analysis) => {
    const analysisResults = parseJsonField(analysis?.analysis_results) || analysis?.analysis_results || {};
    const envelope =
        (analysisResults && typeof analysisResults === 'object' ? analysisResults.analysis : null) ||
        (parseJsonField(analysisResults?.analysis) || analysisResults?.analysis) ||
        analysisResults ||
        parseJsonField(analysis?.analysis) ||
        analysis?.analysis ||
        analysis ||
        {};

    const metadataCandidates = [
        envelope?.metadata,
        parseJsonField(envelope?.metadata),
        analysis?.analysis_results?.metadata,
        analysis?.metadata,
    ];

    let metadata = {};
    for (const candidate of metadataCandidates) {
        const parsed = parseJsonField(candidate) || candidate;
        const normalized = parsed?.metadata || parsed;
        if (normalized && typeof normalized === 'object' && Object.keys(normalized).length > 0) {
            metadata = normalized;
            break;
        }
    }

    const title = String(metadata.title || metadata.Title || metadata.summary_headline || metadata.SummaryHeadline || '').trim();
    const description = String(metadata.description || metadata.Description || '').trim();

    const rawTags =
        (Array.isArray(metadata.tags) && metadata.tags.length > 0
            ? metadata.tags
            : Array.isArray(metadata.Tags) && metadata.Tags.length > 0
                ? metadata.Tags
                : Array.isArray(metadata.suggested_hashtags) && metadata.suggested_hashtags.length > 0
                    ? metadata.suggested_hashtags
                    : metadata.SuggestedHashtags) || [];

    const tags = Array.isArray(rawTags)
        ? rawTags.map((tag) => String(tag || '').trim()).filter(Boolean)
        : [];

    if (title || description || tags.length > 0) {
        return { title, description, tags };
    }

    // Fallback for malformed metadata JSON generated by model outputs.
    return extractMetadataFromRawString(envelope?.metadata);
};

const toSafeVersion = (value, fallback = -1) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

// The workflow carries the only name in the project; the idea and workspace
// have none of their own.
const workflowName = (item) => String(item?.workflow?.Name || '').trim();

function WorkflowDetail() {
    const { workflowId } = useParams();
    const navigate = useNavigate();
    const [workflow, setWorkflow] = useState(null);
    const [currentStep, setCurrentStep] = useState('idea');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // A failed action (rename, finishing a step) is said on the page, next to
    // what failed, rather than in a browser alert that blocks everything.
    const [actionError, setActionError] = useState('');
    // The report's analysis, kept so the next project can start from it.
    const [lastAnalysis, setLastAnalysis] = useState(null);
    const [isStartingNext, setIsStartingNext] = useState(false);
    const [isFinalizing, setIsFinalizing] = useState(false);
    const [postPrefill, setPostPrefill] = useState(null);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');
    const [isSavingTitle, setIsSavingTitle] = useState(false);
    const linkedIdeaId = String(workflow?.idea?.idea_id || workflow?.idea?.id || '').trim();
    const linkedWorkspaceId = String(workflow?.workspace?.VideoWorkspaceId || workflow?.workflow?.WorkspaceId || '').trim();

    useEffect(() => {
        const fetchWorkflow = async () => {
            try {
                setLoading(true);
                const auth = getAuth();
                const idToken = await auth.currentUser.getIdToken();
                const { status, myJson } = await GetWorkflow(idToken, workflowId);

                if (status === 200) {
                    setWorkflow(myJson);
                    setCurrentStep(resolveLandingStep(myJson?.workflow));
                } else {
                    setError('Couldn’t load this project. Refresh to try again.');
                }
            } catch (err) {
                setError(err.message || 'Error loading workflow');
            } finally {
                setLoading(false);
            }
        };

        if (workflowId) {
            fetchWorkflow();
        }
    }, [workflowId]);

    useEffect(() => {
        const hydratePostPrefill = async () => {
            if (!workflow?.workspace?.VideoWorkspaceId) {
                setPostPrefill(null);
                return;
            }

            try {
                const auth = getAuth();
                const idToken = await auth.currentUser.getIdToken();
                const workspace = workflow.workspace || {};
                const workspaceId = String(workspace.VideoWorkspaceId || '').trim();

                // UploadedVersion first, because it is the only one that ever
                // names a rendered cut. Version is the DRAFT slot -- the version
                // the next render will write -- and publishing sets it to
                // UploadedVersion + 1, so asking for the higher of the two asked
                // for an empty slot every single time a version had been
                // published. It fell back and the video still resolved, but the
                // first request was a guaranteed 404 on every page load.
                const draftVersion = toSafeVersion(workspace.Version, -1);
                const uploadedVersion = toSafeVersion(workspace.UploadedVersion, -1);
                const candidateVersions = [uploadedVersion];
                if (draftVersion !== uploadedVersion) {
                    candidateVersions.push(draftVersion);
                }

                let resolvedVideoUrl = '';
                for (const version of candidateVersions) {
                    if (version < 0) continue;
                    const output = await GetVideoWorkspaceOutputUrl(idToken, workspaceId, version);
                    if (output.status === 200 && output.myJson?.OutputUrl) {
                        resolvedVideoUrl = output.myJson.OutputUrl;
                        break;
                    }
                }

                const workspaceAnalysisId = String(workspace.AnalysisId || '').trim();
                const workflowAnalysisId = String(workflow?.workflow?.AnalysisId || '').trim();
                const analysisIdToFetch = workspaceAnalysisId || workflowAnalysisId;

                let analysis = null;
                if (analysisIdToFetch) {
                    const analysisResponse = await GetAnalysisById(idToken, analysisIdToFetch);
                    if (analysisResponse.status === 200 && analysisResponse.myJson) {
                        analysis = analysisResponse.myJson;
                    }
                }

                const metadata = analysis
                    ? extractMetadataFromAnalysis(analysis)
                    : { title: '', description: '', tags: [] };

                setPostPrefill({
                    videoUrl: resolvedVideoUrl,
                    title: metadata.title,
                    description: metadata.description,
                    tags: metadata.tags,
                });
            } catch (prefillError) {
                console.error('Failed to build workflow post prefill:', prefillError);
                setPostPrefill(null);
            }
        };

        hydratePostPrefill();
    }, [workflow]);

    const handleNavigate = (direction) => {
        const stepOrder = ['idea', 'workspace', 'post', 'report'];
        const currentStepIndex = stepOrder.indexOf(currentStep);

        if (direction === 'prev' && currentStepIndex > 0) {
            setCurrentStep(stepOrder[currentStepIndex - 1]);
        }

        if (direction === 'next' && currentStepIndex >= 0 && currentStepIndex < stepOrder.length - 1) {
            setCurrentStep(stepOrder[currentStepIndex + 1]);
        }
    };

    const beginEditTitle = () => {
        setTitleDraft(workflowName(workflow));
        setIsEditingTitle(true);
    };

    const cancelEditTitle = () => {
        setIsEditingTitle(false);
        setTitleDraft('');
    };

    // The project name lives on the workflow itself. It used to be the linked
    // idea's title, which the backend recomputes from the idea text on every
    // save — so writing an idea replaced the name the user had chosen with the
    // first line of their script.
    const saveTitle = async () => {
        if (isSavingTitle) {
            return;
        }

        const nextTitle = String(titleDraft || '').trim();
        if (!nextTitle || nextTitle === workflowName(workflow)) {
            cancelEditTitle();
            return;
        }

        try {
            setIsSavingTitle(true);
            const auth = getAuth();
            const idToken = await auth.currentUser.getIdToken();
            const { status, myJson } = await RenameWorkflow(idToken, workflowId, nextTitle);

            if (status !== 200 || !myJson?.workflow) {
                throw new Error(myJson?.detail?.Message || myJson?.detail || 'Failed to rename workflow');
            }

            setWorkflow(myJson);
            setIsEditingTitle(false);
            setTitleDraft('');
        } catch (err) {
            setActionError('Couldn’t rename this project: ' + (err.message || 'try again in a moment.'));
        } finally {
            setIsSavingTitle(false);
        }
    };

    /* A new project whose idea opens with this video's lessons: the
       analysis headline and its recommendations, offered as notes to keep,
       change or delete. Recommendations stay advice; nothing is applied. */
    const handleStartNextVideo = async () => {
        const previous = workflowName(workflow) || 'your last video';
        const lines = [`Building on “${previous}”.`];
        if (lastAnalysis?.Headline) {
            lines.push('', 'What the last video showed:', lastAnalysis.Headline);
        }
        const recs = Array.isArray(lastAnalysis?.Recommendations) ? lastAnalysis.Recommendations : [];
        if (recs.length > 0) {
            lines.push('', 'Ideas to try (from its report; keep, change or delete them):');
            recs.slice(0, 5).forEach((rec) => {
                const detail = rec.Action || rec.Why || '';
                lines.push(`- ${rec.Title || 'Suggestion'}${detail ? `: ${detail}` : ''}`);
            });
        }
        try {
            setIsStartingNext(true);
            setActionError('');
            const idToken = await getAuth().currentUser.getIdToken();
            const { status, myJson } = await CreateWorkflow(idToken, {
                name: `${previous} — next video`,
                text: lines.join('\n'),
                prompt: '',
                flow_type: 'full',
                collaborators: [],
            });
            if (status === 200 && myJson?.workflow?.WorkflowId) {
                navigate(`/Workflow/${myJson.workflow.WorkflowId}`);
                return;
            }
            setActionError('Couldn’t start the next video. Try again.');
        } catch (err) {
            setActionError('Couldn’t start the next video: ' + (err.message || 'try again in a moment.'));
        } finally {
            setIsStartingNext(false);
        }
    };

    const handleFinalize = async () => {
        try {
            setIsFinalizing(true);
            setActionError('');
            const auth = getAuth();
            const idToken = await auth.currentUser.getIdToken();
            const { status, myJson } = await FinalizeWorkflowStep(idToken, workflowId);

            if (status === 200) {
                // No confirmation popup — finalizing already advances to the next
                // step, so the step change is the feedback.
                setWorkflow(myJson);
                setCurrentStep(normalizeWorkflowStep(myJson?.workflow?.CurrentStep || currentStep));
            } else {
                setActionError('Couldn’t save this step. Try again.');
            }
        } catch (err) {
            setActionError('Couldn’t save this step: ' + (err.message || 'try again in a moment.'));
        } finally {
            setIsFinalizing(false);
        }
    };

    // Publishing ends the Post step. Finalize it if that has not happened yet,
    // then move to the Report either way — re-publishing an already-finalized
    // post used to do nothing at all and left the user on the Post tab.
    const handlePostPublished = async () => {
        const alreadyFinalized = Boolean((workflow?.workflow?.FinalizedSteps || {})[currentStep]);
        if (!alreadyFinalized) {
            await handleFinalize();
        }
        setCurrentStep('report');
    };

    if (loading) {
        return <div className="workflow-detail-container loading">Loading project…</div>;
    }

    if (error) {
        return <div className="workflow-detail-container error">{error}</div>;
    }

    if (!workflow) {
        return <div className="workflow-detail-container">We couldn’t find this project. It may have been deleted, or you may not have access to it.</div>;
    }

    const wf = workflow.workflow;
    const myRole = workflow.my_role || (wf.UserId ? 'owner' : null);
    const canEdit = myRole === 'owner' || myRole === 'editor';
    const isCurrentStepFinalized = (wf.FinalizedSteps || {})[currentStep];
    const stepOrder = ['idea', 'workspace', 'post', 'report'];
    const currentStepIndex = stepOrder.indexOf(currentStep);
    const hasNextStep = currentStepIndex >= 0 && currentStepIndex < stepOrder.length - 1;

    // Render only the current step's object
    const renderCurrentStepContent = () => {
        switch (currentStep) {
            case 'idea':
                return (
                    <Ideation
                        forcedIdeaId={linkedIdeaId}
                        hideWorkflowNav
                        hideHeader
                        hideSidebar
                        lockToIdea={Boolean(linkedIdeaId)}
                    />
                );
            case 'workspace':
                return linkedWorkspaceId ? (
                    <VideoWorkspace
                        forcedWorkspaceId={linkedWorkspaceId}
                        ideaId={linkedIdeaId}
                        projectName={workflowName(workflow)}
                        hideWorkflowNav
                        hideHeader
                        hideSidebar
                        inWorkflow
                    />
                ) : (
                    <div className="step-content-empty">This project has no edit attached yet.</div>
                );
            case 'post':
                return workflow.post?.PostId ? (
                    <VideoUploader
                        forcedPostId={workflow.post.PostId}
                        initialData={postPrefill}
                        // What we think this project is. The backend resolves
                        // the same thing at publish time from the same counter;
                        // this is only so the creator sees the answer before
                        // they commit to it rather than after.
                        aiSuggested={Number(workflow.workspace?.GeneratedVideos || 0) > 0}
                        hideHeader
                        // Publishing is the finalize for this step, so the
                        // composer owns the single action.
                        onPublished={canEdit ? handlePostPublished : null}
                    />
                ) : (
                    <div className="step-content-empty">Post not found</div>
                );
            case 'report':
                return <ProjectReport post={workflow.post} workflowId={workflowId} onAnalysis={setLastAnalysis} />;
            default:
                return <div>Unknown step</div>;
        }
    };

    // What to do in the step you are on, said on its own frame of the strip
    // rather than as a separate paragraph under it.
    const STEP_GUIDANCE = {
        idea: 'Write the script, then Lock the idea',
        workspace: 'Cut your clips, then Use this cut',
        post: 'Publish now or schedule it',
        report: 'See how the post is doing',
    };

    // A fact on every frame, not only the one you are on: whether the idea is
    // locked, which cut is in, where it posted. The frame you are on adds what
    // to do there after its fact; the report frame carries the verdict.
    const frameFacts = {};
    if ((wf.FinalizedSteps || {}).idea) {
        frameFacts.idea = 'Idea locked';
    }
    const cutVersion = Number(workflow.workspace?.Version || 0);
    if (cutVersion > 0) {
        frameFacts.workspace = (wf.FinalizedSteps || {}).workspace ? `Cut v${cutVersion} in` : `Draft v${cutVersion}`;
    }
    if (workflow.workspace?.IsPosted) {
        const postedAt = workflow.workspace.PostedAt ? new Date(workflow.workspace.PostedAt) : null;
        frameFacts.post = postedAt && !Number.isNaN(postedAt.getTime())
            ? `Posted ${postedAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
            : 'Posted';
    } else if (workflow.post?.userChannels?.length) {
        const count = workflow.post.userChannels.length;
        frameFacts.post = `${count} channel${count === 1 ? '' : 's'} picked`;
    }

    // The one next action, at the strip's right end, named for what it does
    // here rather than "Finalize Step". Post finalizes through its own publish
    // button, so it has nothing to offer until that has happened.
    const FINISH_LABELS = {
        idea: 'Lock the idea',
        workspace: 'Use this cut',
    };
    const NEXT_LABELS = {
        idea: 'Go to Edit',
        workspace: 'Go to Post',
        post: 'See the report',
    };
    let stepAction = null;
    if (currentStep === 'report') {
        // The loop's last frame points back at its first: the next video
        // starts from what this one taught, not from a blank page.
        stepAction = (
            <button type="button" className="btn btn-primary" onClick={handleStartNextVideo} disabled={isStartingNext}>
                {isStartingNext ? 'Starting…' : 'Start the next video'}
            </button>
        );
    } else if (!isCurrentStepFinalized && canEdit && currentStep !== 'post') {
        stepAction = (
            <button type="button" className="btn btn-primary" onClick={handleFinalize} disabled={isFinalizing}>
                {isFinalizing ? 'Saving…' : FINISH_LABELS[currentStep] || 'Done with this step'}
            </button>
        );
    } else if (isCurrentStepFinalized && hasNextStep) {
        stepAction = (
            <button type="button" className="btn btn-primary" onClick={() => handleNavigate('next')}>
                {NEXT_LABELS[currentStep] || 'Next'} <ArrowRight size={16} aria-hidden="true" />
            </button>
        );
    }

    return (
        <div className="workflow-detail-container">
            <div className="workflow-detail-header">
                {isEditingTitle ? (
                    <div className="workflow-title-edit">
                        <input
                            className="workflow-title-input"
                            type="text"
                            value={titleDraft}
                            autoFocus
                            maxLength={120}
                            disabled={isSavingTitle}
                            placeholder="Name this project…"
                            onChange={(e) => setTitleDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    saveTitle();
                                }
                                if (e.key === 'Escape') {
                                    e.preventDefault();
                                    cancelEditTitle();
                                }
                            }}
                            onBlur={saveTitle}
                        />
                    </div>
                ) : (
                    <h1 className="workflow-title">
                        {workflowName(workflow) || 'Untitled project'}
                        {canEdit && (
                            <button
                                type="button"
                                className="workflow-title-edit-btn"
                                onClick={beginEditTitle}
                                title="Rename project"
                                aria-label="Rename project"
                            >
                                <Pencil size={14} aria-hidden="true" />
                            </button>
                        )}
                    </h1>
                )}
                {myRole && myRole !== 'owner' && (
                    <span className={`workflow-role-badge role-${myRole}`}>{myRole}</span>
                )}
                <div className="workflow-header-aside">
                <CollaboratorsPanel
                    workflowId={workflowId}
                    myRole={myRole}
                    collaborators={workflow.collaborators || []}
                    onChange={(updated) => setWorkflow(updated)}
                />
                <WorkflowSpend workflowId={workflowId} />
                </div>
            </div>

            <div className="workflow-progress">
                <ProjectStepper
                    currentStep={currentStep}
                    flowType={wf.FlowType || 'full'}
                    finalizedSteps={wf.FinalizedSteps || {}}
                    onStepClick={(step) => {
                        // Allow jumping to any step already reached or finalized.
                        const order = ['idea', 'workspace', 'post', 'report'];
                        const finalized = Boolean((wf.FinalizedSteps || {})[step]);
                        if (order.indexOf(step) <= order.indexOf(currentStep) || finalized) {
                            setCurrentStep(step);
                        }
                    }}
                    action={stepAction}
                    notes={{
                        ...frameFacts,
                        [currentStep]: [frameFacts[currentStep], STEP_GUIDANCE[currentStep]].filter(Boolean).join('. '),
                    }}
                    verdict={lastAnalysis?.Headline || ''}
                />
                {actionError && (
                    <p className="workflow-action-error" role="alert">{actionError}</p>
                )}
            </div>


            <div className={`workflow-content ${(currentStep === 'idea' || currentStep === 'workspace') ? 'workflow-content-ideation' : ''}`}>
                {renderCurrentStepContent()}
            </div>

        </div>
    );
}

export { WorkflowDetail };
